import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';

export interface OverrideSignatureDialogProps {
  open: boolean;
  /**
   * `waive` skips the approval (default); `require` asks the customer for a
   * signature on a work order whose policy does not require one (#4105).
   */
  mode?: 'waive' | 'require';
  submitting: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

export function OverrideSignatureDialog({
  open,
  mode = 'waive',
  submitting,
  onClose,
  onSubmit,
}: OverrideSignatureDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  // Blocks a second submit between the click and the parent's pending flag.
  const inFlight = useRef(false);
  const request = mode === 'require';

  const handleClose = () => {
    if (submitting || inFlight.current) return;
    setReason('');
    setReasonError(false);
    setSubmitError(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (inFlight.current) return;
    const text = reason.trim();
    if (text.length === 0) {
      setReasonError(true);
      return;
    }
    setSubmitError(false);
    inFlight.current = true;
    try {
      await onSubmit(text);
      setReason('');
      setReasonError(false);
    } catch {
      setSubmitError(true);
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      mobileTitle={request ? t('signature.requestTitle') : t('signature.overrideTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{request ? t('signature.requestTitle') : t('signature.overrideTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {request && <Alert severity="info">{t('signature.requestHint')}</Alert>}
          {submitError && <Alert severity="error">{t('signature.saveFailed')}</Alert>}
          <TextField
            label={request ? t('signature.requestReason') : t('signature.overrideReason')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(false);
            }}
            error={reasonError}
            helperText={reasonError ? t('signature.overrideReasonRequired') : undefined}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {request ? t('signature.requestSubmit') : t('signature.overrideSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
